import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  type PuzzleData,
  Cell,
  cellIndex,
  clonePuzzle,
  createBlankPuzzle,
  encodePuzzle,
} from '@/lib/puzzle';
import { TilePuzzleGame } from '@/components/game/TilePuzzleGame';

// ─── Editor tile ────────────────────────────────────────────────────────────

type EditorTool = 'wall' | 'passive' | 'erase' | 'start';

interface EditorTileProps {
  cell: number;
  isStart: boolean;
  tileSize: number;
  onPointerDown: () => void;
  onPointerEnter: () => void;
}

function EditorTile({ cell, isStart, tileSize, onPointerDown, onPointerEnter }: EditorTileProps) {
  const sizeStyle = { width: tileSize, height: tileSize };

  if (cell === Cell.Wall) {
    return (
      <div
        className="rounded-md bg-gray-800 dark:bg-gray-950 border border-gray-900 dark:border-black cursor-pointer"
        style={sizeStyle}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
      />
    );
  }

  if (cell === Cell.Passive && !isStart) {
    return (
      <div
        className="rounded-md bg-gradient-to-br from-gray-300 to-gray-400 border border-gray-200 dark:from-gray-600 dark:to-gray-700 dark:border-gray-500 cursor-pointer hover:ring-2 hover:ring-gray-300 transition-all"
        style={sizeStyle}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
      />
    );
  }

  if (isStart) {
    return (
      <div
        className="rounded-md bg-gradient-to-br from-yellow-400 to-yellow-500 border-2 border-yellow-300 cursor-pointer flex items-center justify-center shadow-md"
        style={sizeStyle}
        onPointerDown={onPointerDown}
        onPointerEnter={onPointerEnter}
      >
        <span className="text-xs font-bold text-yellow-900">S</span>
      </div>
    );
  }

  return (
    <div
      className="rounded-md bg-gradient-to-br from-sky-300 to-sky-400 border border-sky-200 dark:from-sky-600 dark:to-sky-700 dark:border-sky-500 cursor-pointer hover:ring-2 hover:ring-sky-200 transition-all"
      style={sizeStyle}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
    />
  );
}

// ─── Main editor component ──────────────────────────────────────────────────

export interface PuzzleEditorProps {
  initialPuzzle?: PuzzleData;
  onSave?: (puzzle: PuzzleData) => void;
  onPublish?: (puzzle: PuzzleData) => void;
  className?: string;
}

export function PuzzleEditor({ initialPuzzle, onSave, onPublish, className }: PuzzleEditorProps) {
  const [puzzle, setPuzzle] = useState<PuzzleData>(() =>
    initialPuzzle ? clonePuzzle(initialPuzzle) : createBlankPuzzle(6, 6),
  );
  const [tool, setTool] = useState<EditorTool>('wall');
  const [showTest, setShowTest] = useState(false);
  const isPaintingRef = useRef(false);
  const paintModeRef = useRef<'paint' | 'erase' | null>(null);
  const paintValueRef = useRef<Cell>(Cell.Empty);

  // Tile size based on grid dimensions.
  const tileSize = useMemo(() => {
    const maxDim = Math.max(puzzle.width, puzzle.height);
    if (maxDim <= 5) return 56;
    if (maxDim <= 7) return 48;
    if (maxDim <= 10) return 40;
    if (maxDim <= 15) return 32;
    return 28;
  }, [puzzle.width, puzzle.height]);

  // ── Grid manipulation ──────────────────────────────────────────────────────

  const paintCell = useCallback((x: number, y: number, isFirst: boolean) => {
    setPuzzle((prev) => {
      const next = clonePuzzle(prev);
      const idx = cellIndex(next, x, y);

      if (tool === 'start') {
        // Start can be placed on any non-wall tile.
        if (next.cells[idx] === Cell.Wall) return prev;
        next.startX = x;
        next.startY = y;
        return next;
      }

      // Don't paint over the start tile with a wall or passive tile.
      if (x === prev.startX && y === prev.startY && (tool === 'wall' || tool === 'passive')) {
        return prev;
      }

      if (tool === 'erase') {
        next.cells[idx] = Cell.Empty;
      } else if (tool === 'wall' || tool === 'passive') {
        const paintValue = tool === 'wall' ? Cell.Wall : Cell.Passive;
        // On first tile, determine paint mode (toggle).
        if (isFirst && paintModeRef.current === null) {
          if (prev.cells[idx] === paintValue) {
            paintModeRef.current = 'erase';
          } else {
            paintModeRef.current = 'paint';
            paintValueRef.current = paintValue;
          }
        }
        if (paintModeRef.current === 'paint') {
          next.cells[idx] = paintValueRef.current;
        } else if (paintModeRef.current === 'erase') {
          next.cells[idx] = Cell.Empty;
        }
      }

      return next;
    });
  }, [tool]);

  const handlePointerDown = useCallback((x: number, y: number) => {
    isPaintingRef.current = true;
    paintModeRef.current = null;
    paintValueRef.current = Cell.Empty;
    paintCell(x, y, true);
  }, [paintCell]);

  const handlePointerEnter = useCallback((x: number, y: number) => {
    if (isPaintingRef.current) {
      paintCell(x, y, false);
    }
  }, [paintCell]);

  // Stop painting on pointer up.
  useEffect(() => {
    const stop = () => {
      isPaintingRef.current = false;
      paintModeRef.current = null;
      paintValueRef.current = Cell.Empty;
    };
    window.addEventListener('pointerup', stop);
    return () => window.removeEventListener('pointerup', stop);
  }, []);

  // ── Grid resize ─────────────────────────────────────────────────────────────

  const resize = useCallback((dw: number, dh: number) => {
    setPuzzle((prev) => {
      const newWidth = Math.max(2, Math.min(20, prev.width + dw));
      const newHeight = Math.max(2, Math.min(20, prev.height + dh));
      if (newWidth === prev.width && newHeight === prev.height) return prev;

      const newCells = new Array(newWidth * newHeight).fill(Cell.Empty);
      for (let y = 0; y < Math.min(prev.height, newHeight); y++) {
        for (let x = 0; x < Math.min(prev.width, newWidth); x++) {
          newCells[y * newWidth + x] = prev.cells[y * prev.width + x];
        }
      }

      let startX = prev.startX;
      let startY = prev.startY;
      if (startX >= newWidth) startX = newWidth - 1;
      if (startY >= newHeight) startY = newHeight - 1;
      if (newCells[startY * newWidth + startX] === Cell.Wall) {
        newCells[startY * newWidth + startX] = Cell.Empty;
      }

      return { ...prev, width: newWidth, height: newHeight, cells: newCells, startX, startY };
    });
  }, []);

  const clearGrid = useCallback(() => {
    setPuzzle((prev) => ({
      ...prev,
      cells: new Array(prev.width * prev.height).fill(Cell.Empty),
    }));
  }, []);

  const fillWalls = useCallback(() => {
    setPuzzle((prev) => {
      const cells = [...prev.cells];
      for (let i = 0; i < cells.length; i++) {
        const x = i % prev.width;
        const y = Math.floor(i / prev.width);
        if (x === 0 || x === prev.width - 1 || y === 0 || y === prev.height - 1) {
          if (!(x === prev.startX && y === prev.startY)) {
            cells[i] = Cell.Wall;
          }
        }
      }
      return { ...prev, cells };
    });
  }, []);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let activatable = 0;
    let passive = 0;
    for (const c of puzzle.cells) {
      if (c === Cell.Empty) activatable++;
      else if (c === Cell.Passive) passive++;
    }
    return { activatable, passive };
  }, [puzzle.cells]);

  // ── Share URL ─────────────────────────────────────────────────────────────

  const shareUrl = useMemo(() => {
    const encoded = encodePuzzle(puzzle);
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/play?p=${encoded}`;
    }
    return `/play?p=${encoded}`;
  }, [puzzle]);

  const copyShareUrl = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
    }
  }, [shareUrl]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (showTest) {
    return (
      <div className={cn('flex flex-col items-center gap-4', className)}>
        <div className="w-full flex items-center justify-between">
          <h3 className="text-lg font-semibold">Testing: {puzzle.title}</h3>
          <Button variant="outline" onClick={() => setShowTest(false)}>
            Back to Editor
          </Button>
        </div>
        <TilePuzzleGame puzzle={puzzle} />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {/* Title & description */}
      <div className="flex flex-col gap-3">
        <div>
          <Label htmlFor="puzzle-title">Puzzle Title</Label>
          <Input
            id="puzzle-title"
            value={puzzle.title}
            onChange={(e) => setPuzzle((p) => ({ ...p, title: e.target.value }))}
            placeholder="Untitled Puzzle"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="puzzle-desc">Description (optional)</Label>
          <Textarea
            id="puzzle-desc"
            value={puzzle.description ?? ''}
            onChange={(e) => setPuzzle((p) => ({ ...p, description: e.target.value || undefined }))}
            placeholder="Describe your puzzle, give hints, or tell a story..."
            className="mt-1 resize-none"
            rows={2}
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
          <Button
            size="sm"
            variant={tool === 'wall' ? 'default' : 'ghost'}
            onClick={() => setTool('wall')}
          >
            Wall
          </Button>
          <Button
            size="sm"
            variant={tool === 'passive' ? 'default' : 'ghost'}
            onClick={() => setTool('passive')}
          >
            Passive
          </Button>
          <Button
            size="sm"
            variant={tool === 'erase' ? 'default' : 'ghost'}
            onClick={() => setTool('erase')}
          >
            Erase
          </Button>
          <Button
            size="sm"
            variant={tool === 'start' ? 'default' : 'ghost'}
            onClick={() => setTool('start')}
          >
            Start
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => resize(-1, 0)} disabled={puzzle.width <= 2}>
            {'\u2190 W'}
          </Button>
          <span className="text-sm font-semibold tabular-nums px-2">{puzzle.width}{'\u00d7'}{puzzle.height}</span>
          <Button size="sm" variant="outline" onClick={() => resize(1, 0)} disabled={puzzle.width >= 20}>
            {'W \u2192'}
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => resize(0, -1)} disabled={puzzle.height <= 2}>
            {'\u2191 H'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => resize(0, 1)} disabled={puzzle.height >= 20}>
            {'H \u2193'}
          </Button>
        </div>

        <Button size="sm" variant="ghost" onClick={clearGrid}>Clear</Button>
        <Button size="sm" variant="ghost" onClick={fillWalls}>Border</Button>
      </div>

      {/* Somaria toggle */}
      <div className="flex items-center gap-3">
        <Switch
          checked={puzzle.somaria}
          onCheckedChange={(checked) => setPuzzle((p) => ({ ...p, somaria: checked }))}
          id="somaria-toggle"
        />
        <Label htmlFor="somaria-toggle" className="cursor-pointer">
          Cane of Somaria <span className="text-muted-foreground text-sm">(creates obstacles)</span>
        </Label>
      </div>

      {/* Grid */}
      <div className="overflow-auto">
        <div
          className="inline-block rounded-2xl bg-slate-100 dark:bg-slate-800 p-3 shadow-lg touch-none select-none"
        >
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${puzzle.width}, ${tileSize}px)`,
              gridTemplateRows: `repeat(${puzzle.height}, ${tileSize}px)`,
            }}
          >
            {puzzle.cells.map((cell, i) => {
              const x = i % puzzle.width;
              const y = Math.floor(i / puzzle.width);
              return (
                <EditorTile
                  key={i}
                  cell={cell}
                  isStart={x === puzzle.startX && y === puzzle.startY}
                  tileSize={tileSize}
                  onPointerDown={() => handlePointerDown(x, y)}
                  onPointerEnter={() => handlePointerEnter(x, y)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Stats & actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {stats.activatable} activatable tiles{stats.passive > 0 && `, ${stats.passive} passive`}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowTest(true)}>
            Test Play
          </Button>
          <Button variant="outline" onClick={copyShareUrl}>
            Copy Share URL
          </Button>
          {onSave && (
            <Button variant="secondary" onClick={() => onSave(puzzle)}>
              Save
            </Button>
          )}
          {onPublish && (
            <Button onClick={() => onPublish(puzzle)}>
              Publish to Nostr
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
